<template>
  <v-dialog v-model="dialogVisible" max-width="600px">
    <v-card>
      <v-card-title class="text-h5">{{
        esParejas ? "Registrar pareja" : "Registrar Equipo"
      }}</v-card-title>
      <v-card-text v-if="esParejas">
        <p>Registra a tu pareja para el torneo eligiendo a tu compañero.</p>
        <p>
          Tú serás el capitán de la pareja y quien meta los resultados. Amon Sûl
          te añadirá automáticamente a tu pareja.
        </p>
      </v-card-text>
      <v-card-text :class="{ 'pt-0': esParejas }">
        <template v-if="!esParejas">
          <p>Registra a tu equipo para el torneo.</p>
          <p>Debes añadir un máximo de {{ maxPlayers - 1 }} participantes.</p>
          <p>
            Tu serás el capitán del equipo, Amon Sûl te añadirá automáticamente
            a tu equipo.
          </p>
        </template>

        <v-text-field
          v-model="teamName"
          :label="esParejas ? 'Nombre de la pareja' : 'Nombre del equipo'"
          tonal
          required
        ></v-text-field>

        <v-form>
          <v-combobox
            v-model="jugadorSelected"
            :items="jugadores"
            item-title="nick"
            item-value="idUsuario"
            :label="esParejas ? 'Selecciona a tu compañero' : 'Selecciona un jugador'"
            tonal
            required
            @update:modelValue="addPlayer"
            :return-object="true"
            :allow-new="false"
            :loading="loading"
          ></v-combobox>
          <v-progress-linear
            v-if="loading"
            indeterminate
            color="primary"
            height="2"
            class="mt-1"
          />

          <div v-show="selectedPlayers.length">
            <p>{{ esParejas ? "Compañero:" : "Jugadores seleccionados:" }}</p>
            <v-chip
              v-for="(player, index) in selectedPlayers"
              :key="player.idUsuario"
              closable
              @click:close="removePlayer(index)"
              class="ma-1"
            >
              {{ player.nick }}
            </v-chip>
          </div>

          <!-- El tuyo: cada compañero lo decide en su inscripción -->
          <v-checkbox
            v-model="compartirEmail"
            color="primary"
            density="compact"
            hide-details="auto"
            class="mt-2"
            label="Compartir mi correo con el organizador"
            messages="Para que pueda contactarte si hace falta. Puedes cambiarlo cuando quieras en Mi inscripción."
          />

          <CasillaPrivacidad v-model="aceptaPrivacidad" class="mt-2" />
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          variant="tonal"
          color="primary"
          @click="submitForm"
          :disabled="isRegistering || !aceptaPrivacidad"
          >{{ esParejas ? "Registrar pareja" : "Registrar equipo" }}</v-btn
        >
        <v-btn variant="tonal" color="secondary" @click="closeDialog"
          >Cerrar</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>

  <ModalSuccess
    :isVisible="showSuccessModal"
    :message="esParejas ? 'Pareja registrada con éxito.' : 'Equipo registrado con éxito.'"
    @update:isVisible="showSuccessModal = $event"
  />
  <ModalError
    :isVisible="showErrorModal"
    :message="`No se ha podido registrar ${esParejas ? 'la pareja' : 'el equipo'}. Contacta con el administrador.`"
    @update:isVisible="showErrorModal = $event"
  />

  <v-dialog v-model="isRegistering" persistent width="300">
    <v-card>
      <v-card-text
        class="d-flex justify-center align-center"
        style="height: 150px"
      >
        <v-progress-circular
          indeterminate
          color="blue-lighten-3"
          :size="57"
        ></v-progress-circular>
      </v-card-text>
    </v-card>
  </v-dialog>

  <v-snackbar
    color="red-darken-2"
    :timeout="snackBarTimeOut"
    v-model="snackBarShow"
    eager
    auto-height
  >
    {{ snackBarMessage }}
  </v-snackbar>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, onMounted } from "vue";
import { useAuth } from "@/composables/useAuth";
import { registrarEquipo } from "@/services/InscripcionesService";
import { getUsuariosNoInscritosTorneoAsync } from "@/services/UsuariosService";
import { InscripcionEquipoDTO } from "@/interfaces/Inscripcion";
import { UsuarioSinEquipoDTO } from "@/interfaces/Usuario";
import CasillaPrivacidad from "@/components/Commons/CasillaPrivacidad.vue";

const props = defineProps<{
  isVisible: boolean;
  tipoTorneo: string | undefined;
  idTorneo: number;
}>();
const emit = defineEmits(["update:isVisible"]);

const teamName = ref("");
const jugadorSelected = ref<UsuarioSinEquipoDTO | null>(null);
const jugadores = ref<UsuarioSinEquipoDTO[]>([]);
const selectedPlayers = ref<UsuarioSinEquipoDTO[]>([]);
const idsSelected = ref<number[]>([]);
const isRegistering = ref<boolean>(false);
const showSuccessModal = ref<boolean>(false);
const showErrorModal = ref<boolean>(false);
const snackBarShow = ref<boolean>(false);
const snackBarMessage = ref<string>("");
const snackBarTimeOut = ref<number>(3000);
const loading = ref<boolean>(false);
// Sin marcar: el correo solo se comparte si el capitán lo elige
const compartirEmail = ref<boolean>(false);
const aceptaPrivacidad = ref<boolean>(false);

const { getidUsuario } = useAuth();
const idUsuarioLogger = ref<string | null>(getidUsuario.value);

const esParejas = computed(() => props.tipoTorneo === "Parejas");

const maxPlayers = computed(() => {
  return props.tipoTorneo === "Parejas" || props.tipoTorneo === "Equipos_2"
    ? 2
    : props.tipoTorneo === "Equipos_4"
    ? 4
    : props.tipoTorneo === "Equipos_6"
    ? 6
    : 0;
});

const dialogVisible = computed({
  get: () => props.isVisible,
  set: (value) => emit("update:isVisible", value),
});

const addPlayer = (player: UsuarioSinEquipoDTO) => {
  if (
    !player ||
    typeof player === "string" ||
    !player.idUsuario ||
    !player.nick
  ) {
    jugadorSelected.value = null;
    return;
  }

  if (
    player &&
    !selectedPlayers.value.some((p) => p.idUsuario === player.idUsuario)
  ) {
    if (selectedPlayers.value.length >= maxPlayers.value - 1) {
      snackBarMessage.value = esParejas.value
        ? "Una pareja solo tiene un compañero."
        : `Solo puedes añadir un máximo de ${maxPlayers.value - 1} jugadores.`;
      snackBarShow.value = true;
      jugadorSelected.value = null;
      return;
    }
    selectedPlayers.value.push(player);
    idsSelected.value.push(player.idUsuario);
  }
  jugadorSelected.value = null;
};

const removePlayer = (index: number) => {
  selectedPlayers.value.splice(index, 1);
  idsSelected.value.splice(index, 1);
};

const validateForm = () => {
  if (!teamName.value.trim()) {
    snackBarMessage.value = esParejas.value
      ? "El nombre de la pareja es obligatorio"
      : "El nombre del equipo es obligatorio";
    snackBarShow.value = true;
    return false;
  }
  if (!props.idTorneo) {
    showErrorModal.value = true;
    return false;
  }
  return true;
};

const submitForm = async () => {
  if (!validateForm()) return;
  if (props.idTorneo && idUsuarioLogger.value) {
    const id = parseInt(idUsuarioLogger.value);
    const inscripcionEquipo: InscripcionEquipoDTO = {
      idTorneo: props.idTorneo,
      idCapitan: id,
      nombreEquipo: teamName.value,
      miembros: idsSelected.value,
      compartirEmail: compartirEmail.value,
      idInscripcion: 0,
      idUsuario: 0,
      idEquipo: 0,
      idOrganizador: 0,
      emailOrganizador: "",
      componentesEquipoDTO: [],
    };
    try {
      isRegistering.value = true;
      await registrarEquipo(inscripcionEquipo);
      showSuccessModal.value = true;
      emit("update:isVisible", false);
      window.location.reload();
    } catch (error) {
      showErrorModal.value = true;
    } finally {
      isRegistering.value = false;
    }
  } else {
    showErrorModal.value = true;
  }
};

const closeDialog = () => {
  emit("update:isVisible", false);
  teamName.value = "";
  selectedPlayers.value = [];
  idsSelected.value = [];
  compartirEmail.value = false;
  aceptaPrivacidad.value = false;
};

onMounted(async () => {
  loading.value = true;
  try {
    const responseJugadores = await getUsuariosNoInscritosTorneoAsync(
      props.idTorneo
    );
    jugadores.value = (responseJugadores?.data ?? []).sort(
      (a: { nick: string }, b: { nick: string }) => a.nick.localeCompare(b.nick)
    );
  } catch (error) {
    console.error("Error al obtener jugadores: ", error);
    jugadores.value = [];
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped></style>
